import React from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { VALUES, SKILLS, skillLabel } from "@/lib/constants";
import { VOLUNTEER } from "@/constants/testIds";
import { Sparkles, LocateFixed, X } from "lucide-react";

// Search/sort row, smart-search hint and the geo-filter panel for
// /opportunities (extracted from Opportunities.jsx in the Phase 15 component
// split — behaviour unchanged).

export function SearchSortRow({ q, setQ, value, setValue, skill, setSkill, sort, setSort, qDebounced }) {
  return (
    <div className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-3">
      <Input
        data-testid="opps-search-input"
        id={VOLUNTEER.oppSearch}
        placeholder="Search title, description, organisation…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="h-11"
      />
      <Select value={value} onValueChange={setValue}>
        <SelectTrigger data-testid={VOLUNTEER.oppFilterValue} className="h-11 min-w-[140px]"><SelectValue placeholder="Any value" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Any value</SelectItem>
          {VALUES.map(v => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={skill} onValueChange={setSkill}>
        <SelectTrigger data-testid={VOLUNTEER.oppFilterSkill} className="h-11 min-w-[140px]"><SelectValue placeholder="Any skill" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Any skill</SelectItem>
          {SKILLS.map(s => <SelectItem key={s} value={s} className="capitalize">{skillLabel(s)}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={sort || (qDebounced ? "relevance" : "event_date_asc")} onValueChange={setSort}>
        <SelectTrigger data-testid="opps-sort-select" className="h-11 min-w-[150px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          {qDebounced && <SelectItem value="relevance">Best match</SelectItem>}
          <SelectItem value="event_date_asc">Soonest first</SelectItem>
          <SelectItem value="created_desc">Newest first</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

export function SmartSearchHint({ user }) {
  const hasProfileText = user && (
    (user.bio && user.bio.trim()) ||
    (user.skills && user.skills.length > 0) ||
    (user.values && user.values.length > 0)
  );
  if (user && hasProfileText) {
    return (
      <div data-testid="smart-search-hint" className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-forest/70 font-bold">
        <Sparkles className="w-3.5 h-3.5 text-terracotta" /> Smart search enabled
      </div>
    );
  }
  if (!user) {
    return (
      <Link to="/login" data-testid="smart-search-hint" className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-forest/50 font-bold hover:text-forest">
        <Sparkles className="w-3.5 h-3.5" /> Sign in for smart search
      </Link>
    );
  }
  return null;
}

export function GeoPanel({
  user, remote, setRemote, hasCapacity, setHasCapacity,
  dbs, setDbs,
  center, radius, setRadius, matchingCount,
  onUseMyLocation, onUseProfileLocation, onClearGeo,
}) {
  return (
    <div className="mt-4 bg-white border border-neutral-200 rounded-xl p-5">
      <div className="flex items-center gap-3 flex-wrap">
        <button
          data-testid="filter-toggle-remote"
          onClick={() => setRemote(r => !r)}
          className={`text-sm px-4 py-2 rounded-full border transition-colors ${remote ? "bg-forest text-white border-forest" : "bg-white border-neutral-200 text-neutral-700 hover:border-forest/40"}`}
        >
          Remote only
        </button>
        <button
          data-testid="filter-toggle-has-capacity"
          onClick={() => setHasCapacity(v => !v)}
          className={`text-sm px-4 py-2 rounded-full border transition-colors ${hasCapacity ? "bg-forest text-white border-forest" : "bg-white border-neutral-200 text-neutral-700 hover:border-forest/40"}`}
        >
          Has spots
        </button>
        {/* B4: DBS filter — any / hide DBS-required roles / only DBS roles */}
        <Select value={dbs} onValueChange={setDbs}>
          <SelectTrigger data-testid="filter-dbs-select" className={`h-[38px] w-auto rounded-full text-sm ${dbs !== "any" ? "bg-forest text-white border-forest" : ""}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">DBS: any</SelectItem>
            <SelectItem value="no">No DBS needed</SelectItem>
            <SelectItem value="only">DBS required only</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="geo-use-current" variant="outline" onClick={onUseMyLocation} className="rounded-full">
          <LocateFixed className="w-4 h-4 mr-1.5" /> Use my current location
        </Button>
        {user && (
          <Button data-testid="geo-use-saved" variant="outline" onClick={onUseProfileLocation} className="rounded-full">
            Use saved location
          </Button>
        )}
        {center && (
          <button data-testid="geo-clear" onClick={onClearGeo} className="text-xs text-neutral-500 hover:text-forest inline-flex items-center gap-1">
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        )}
      </div>
      {center && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Within {radius} km of {center.label}</div>
            <div className="text-xs text-neutral-500">{matchingCount} matching</div>
          </div>
          <Slider
            data-testid="geo-radius-slider"
            value={[radius]}
            onValueChange={(v) => setRadius(v[0])}
            min={1} max={200} step={1}
            className="max-w-md"
          />
        </div>
      )}
    </div>
  );
}
